import React, { useEffect, useState } from 'react';
import { Typography, Select, FormControl, MenuItem, InputLabel, Snackbar, Alert } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import ControlledFormDataGrid from "../../../components/DataGrid/ControlledFormDataGrid";
import moment from "moment";
import axios from 'axios'
import { APIURL } from '../../../config';
import { convertTime } from 'src/utils/time';

export default function ReportBuilder(props) {
    const apiRef = useGridApiRef();

    const [subscribedForms, setSubscribedForms] = useState([]);
    const [Form_Reports, setForm_Reports] = useState([]);
    const [formID, setFormID] = useState('');
    const [formName, setFormName] = useState('');
    const [reportID, setReportID] = useState('');
    const [reportFormID, setReportFormID] = useState('');
    const [reportName, setReportName] = useState('');
    const [formData, setFormData] = useState([]);
    const [formColumns, setFormColumns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState(null);

    const handleCloseSnackbar = () => setSnackbar(null);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const subscribedForms = await axios.get(`${APIURL}/task_form/subscribed?company_id=${props.comp_id}`);
                const Form_Reports = await axios.get(APIURL + '/forms/form_report', { params: { report_company_id: props.comp_id } });
                setSubscribedForms(subscribedForms.data);
                setForm_Reports(Form_Reports.data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching initial report builder data:', error);
            }
        };
        fetchInitialData();
    }, []);

    useEffect(() => {
        const fetchFormData = async () => {
            if (formID || reportFormID) {
                try {
                    const formsWithData = await axios.get(APIURL + '/customer_form/formsWithData/0', { params: { compID: props.comp_id, formID: formID || reportFormID } });
                    if (formsWithData.data.length === 0) {
                        setSnackbar({ children: 'No data available!', severity: 'error' });
                        return;
                    }
                    let formComponents;
                    let rows = formsWithData.data.map((data, idx) => {
                        formComponents = data?.translated_json_schema ? data?.translated_json_schema.components : data.json_schema.components;
                        return {
                            id: idx,
                            cus_name: data.cus_name,
                            cus_local_id: data.cus_local_id || '-',
                            emp_name: data.emp_first_name ? data.emp_first_name + ' ' + data.emp_last_name : '-',
                            emp_local_id: data.emp_local_id || '-',
                            tas_title: data.tas_title,
                            tas_type: data.tas_type,
                            tas_start_time: convertTime(data.empLocdate, data.empLocate_timezone, moment.tz.guess(), "MM/DD/YYYY hh:mm A") ?? moment(data.empLocdate).utc().format('MM/DD/YYYY hh:mm A'),
                            tas_end_time: convertTime(data.checkoutEmpDate, data.empLocate_timezone, moment.tz.guess(), "MM/DD/YYYY hh:mm A") ?? moment(data.checkoutEmpDate).utc().format('MM/DD/YYYY hh:mm A'),
                            tas_loc: data.locInfo?.[0] ? data.locInfo[0].loc_addr_line1 + ', ' + data.locInfo[0].loc_addr_line2 + ', ' + data.locInfo[0].loc_city + ', ' + data.locInfo[0].loc_state + ' ' + data.locInfo[0].loc_zip : '-',
                            ...data.formData.data,
                        };
                    });

                // Case: If Select Boxes component is used from Form Builder
                rows = rows.map(row => {
                    for (let key in row) {
                        if (typeof row[key] === 'object' && row[key] !== null) {
                            row[key] = Object.keys(row[key])
                                .filter(k => row[key][k])
                                .join(', ');
                        }
                    }
                    return row;
                });

                let columns = [
                    { field: "id", headerName: "ID", hide: true },
                    { field: "cus_name", headerName: "Customer Name", width: 150 },
                    { field: "cus_local_id", headerName: "Local Customer ID", width: 150 },
                    { field: "emp_name", headerName: "Employee Name", width: 150 },
                    { field: "emp_local_id", headerName: "Local Employee ID", width: 150 },
                    { field: "tas_title", headerName: "Task Name", width: 200 },
                    { field: "tas_type", headerName: "Task Type", width: 100 },
                    { field: "tas_start_time", headerName: "Task Start Date", width: 150, },
                    { field: "tas_end_time", headerName: "Task End Date", width: 150, },
                    { field: "tas_loc", headerName: "Task Location", width: 200, wrap: true },
                ];

                if (formComponents) {
                    formComponents.forEach((component) => {
                        if (component.key !== 'submit' && component.input) {
                            columns.push({ field: component.key, headerName: component.label });
                        }
                    })
                }

                setFormColumns(columns);
                setFormData(rows);
            } catch (error) {
                setSnackbar({ children: error.message, severity: 'error' });
            }
        }
    };
    fetchFormData();
}, [formID, reportFormID]);

    const handleFormChange = (event) => {
        setFormData([]);
        setFormColumns([]);
        setReportID('');
        setReportFormID('');
        setReportName('');

        setFormName(event.target.value);
    };

    const handleFormMenuItemClick = (event) => {
        setFormData([]);
        setFormColumns([]);
        setReportID('');
        setReportFormID('');
        setReportName('');

        setFormID(event.target.id);
    }

    const handleReportChange = (event) => {
        setFormData([]);
        setFormColumns([]);
        setFormID('');
        setFormName('');

        setReportName(event.target.value);
    };

    const handleReportMenuItemClick = (event) => {
        setFormData([]);
        setFormID('');
        setFormName('');

        setReportFormID(Form_Reports.filter((report) => report.report_id === parseInt(event.target.id))[0].report_form_id)
        setReportID(event.target.id);
    }

    return (
        <div style={{ width: '100%' }}>
            {!!loading && (
                <div className="d-flex flex-row w-100 justify-content-center mt-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            )}
            {!loading &&
                <div>
                    <div style={{ display: 'flex', marginBottom: 20 }}>
                        <div style={{ marginRight: 40 }}>
                            <FormControl sx={{ minWidth: 210 }} size="small">
                                <InputLabel id="employeeSelectLabel">Create a new report</InputLabel>
                                <Select
                                    labelId="formSelectLabel"
                                    id="formSelect"
                                    value={formName}
                                    label="Create a new report"
                                    onChange={handleFormChange}
                                >
                                    {subscribedForms.map((form) => (
                                        <MenuItem
                                            id={form.form_id}
                                            key={form.form_id}
                                            value={form.form_name}
                                            onClick={handleFormMenuItemClick}
                                        >
                                            {form.form_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                        <h4 style={{ marginRight: 40, alignContent: 'center' }}>or</h4>
                        <div style={{ marginRight: 40 }}>
                            <FormControl sx={{ minWidth: 210 }} size="small">
                                <InputLabel id="employeeSelectLabel">Edit an existing report</InputLabel>
                                <Select
                                    labelId="custSelectLabel"
                                    id="custSelect"
                                    value={reportName}
                                    label="Edit an existing report"
                                    onChange={handleReportChange}
                                >
                                    {Form_Reports.map((report) => (
                                        <MenuItem
                                            id={report.report_id}
                                            key={report.report_id}
                                            value={report.report_name}
                                            onClick={handleReportMenuItemClick}
                                        >
                                            {report.report_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    </div>
                    {formData.length !== 0 &&
                        <ControlledFormDataGrid
                            isReportBuilder={true}
                            formID={formID}
                            reportID={reportID}
                            user={props.user}
                            apiRef={apiRef}
                            getRowId={(row) => row.id}
                            rows={formData}
                            columns={formColumns}
                            checkboxSelection={false}
                            disableRowSelectionOnClick
                        />
                    }
                    {!!snackbar && (
                        <Snackbar
                            open
                            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                            onClose={handleCloseSnackbar}
                            autoHideDuration={6000}
                        >
                            <Alert {...snackbar} onClose={handleCloseSnackbar} />
                        </Snackbar>
                    )}
                </div>
            }
        </div>
    )
};